import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  TemplateRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IContentList } from 'src/app/core/models/i-content-list';
import { Subscription, Subject, Observable } from 'rxjs';
import { NotificationBarComponent } from 'src/app/shared/notification-bar/notification-bar.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { IContent } from '../../core/models/i-content';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ViewContentListComponent } from '../view-content-list/view-content-list.component';
import {
  IUpdateContentList,
  UpdateContentList,
  ContentToUpdate,
} from '../../core/models/i-update-content-list';
import { ContentToPin } from '../../core/models/i-content-pinned';
import { delay, take } from 'rxjs/operators';
import { AddContentListComponent } from '../add-content-list/add-content-list.component';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { Router } from '@angular/router';
import { UserRolesService } from 'src/app/shared/services/user-roles.service';
import { USER_ROLES } from '../../core/constants/cms-user-roles';
import { ContentListService } from '../shared/services/cmsapi/content-list.service';
import { ContentListStateService } from '../shared/services/cmsapi/content-list-state.service';
import { AddTopicsToContentListComponent } from '../add-topics-to-content-list/add-topics-to-content-list.component';
import { ITopicBase } from 'src/app/core/models/i-topic-base';
import { CmsConfirmationDialogComponent } from '../shared/components/cms-confirmation-dialog/cms-confirmation-dialog.component';
import { ITag } from 'src/app/core/models/i-tag';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';
import { WithNameFormSection } from 'src/app/shared/mixins/with-name-form';
import { FORM_PATHS } from '../shared/constants/form-paths';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FORM_FIELDS } from '../shared/constants/form-fields';

class EditContentListComponentBase {
  FORM_PATHS = FORM_PATHS;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});
  constructor(
    public formBuilder: FormBuilder,
    public contentListStateService: ContentListStateService
  ) {}
}

const EditContentListComponentMixinBase = WithNameFormSection(
  EditContentListComponentBase
);

@Component({
  selector: 'app-edit-content-list',
  templateUrl: './edit-content-list.component.html',
})
export class EditContentListComponent
  extends EditContentListComponentMixinBase
  implements OnInit, OnDestroy
{
  contentList: IContentList;
  @ViewChild(ViewContentListComponent)
  viewContentListComponent: ViewContentListComponent;
  @ViewChild(AddContentListComponent)
  addContentListComponent: AddContentListComponent;
  @ViewChild(AddTopicsToContentListComponent)
  addTopicsToContentListComponent: AddTopicsToContentListComponent;
  @ViewChild('formChangeWarn', { static: true })
  formChangeWarn: TemplateRef<any>;

  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;

  updateContentListRequest: IUpdateContentList = new UpdateContentList();
  changesDiscarded: Subject<boolean> = new Subject<boolean>();
  submitted: boolean;
  deleteSubmitted: boolean;
  updateTimerDuration = 2000;
  pageSubscriptions: Subscription[] = [];
  isContentListOutOfDate = false;
  contentListCode = '';
  displayCode = false;
  displayDeleteButton = false;
  isDefaultList: boolean;
  contentListNameSuffix: string;
  contentListNamePrefix: string;
  isEditTitle: boolean = false;

  form = this.buildForm();

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      [this.FORM_FIELDS.NAME]: [''],
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): boolean {
    if (
      this.contentListStateService.isContentListChanged() &&
      !this.isContentListOutOfDate
    ) {
      event.returnValue = true;
      event.preventDefault();
      return false;
    }
  }

  constructor(
    public formBuilder: FormBuilder,
    public alertService: AlertService,
    public notificationBar: MatSnackBar,
    public canDeactivateDialog: MatDialog,
    public contentListStateService: ContentListStateService,
    private accountsService: CitywireInternalAccountsService,
    private contentListService: ContentListService,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private router: Router,
    private userRolesService: UserRolesService
  ) {
    super(formBuilder, contentListStateService);
  }

  ngOnInit(): void {
    this.userRolesService
      .isUserInRole(USER_ROLES.CanDeleteContentList)
      .subscribe((isInRole) => {
        if (isInRole) {
          this.displayDeleteButton = true;
        }
      });

    const contentListId = this.route.snapshot.paramMap.get('id');
    this.contentListCode = this.getGlobeFormattedContentListCode(contentListId);
    const currentUserNameSubscription = this.accountsService.currentUser
      .pipe(take(1))
      .subscribe(
        (currentUser) =>
          (this.updateContentListRequest.user =
            currentUser.username.toLowerCase())
      );

    const contentListSubscription = this.contentListService
      .getById$(contentListId)
      .subscribe(
        (contentList) => {
          let nameSplit = contentList.name.split(':');
          if (nameSplit.length === 2) {
            this.contentListNamePrefix = nameSplit[0];
            this.contentListNameSuffix = nameSplit[1].trim();
          } else {
            this.contentListNameSuffix = nameSplit[0];
          }

          this.contentList = contentList;
          this.updateContentListRequest.topics = contentList.topics;
          this.updateContentListRequest.tags = contentList.tags;
          const contentListType = contentList.contentListType;
          this.isDefaultList = contentListType === 'DefaultList';
          this._initName(this.contentListNameSuffix);
          this._initNameValueChanges(this.getName);
        },
        (error) => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the content list. Please contact IT.',
          });
        }
      );

    this.form.disable();

    this.pageSubscriptions.push(
      contentListSubscription,
      currentUserNameSubscription
    );
  }

  toggleDisplayCode(): void {
    this.displayCode = !this.displayCode;
  }

  toggleEditTitle() {
    this.isEditTitle = !this.isEditTitle;
    if (this.isEditTitle) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  getContentListCodeTooltip(): string {
    return this.displayCode
      ? 'Hide content list code'
      : 'Show content list code';
  }

  getGlobeFormattedContentListCode(contentListCode: string): string {
    return contentListCode
      ? contentListCode.replace('ContentList_', '').replace(/_+/g, '-')
      : contentListCode;
  }

  copyContentListCodeToClipboard(): void {
    const textArea = document.createElement('textarea');
    textArea.value = this.contentListCode;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();

    this.notificationBar.openFromComponent(NotificationBarComponent, {
      data: this.contentListCode + ' was successfully copied to clipboard!',
    });
  }

  formatContentListName(name: string): string {
    return name ? name.replace('-', '>') : name;
  }

  triggerAddToContentList(contents: IContent[]): void {
    if (!contents.length) {
      this.notificationBar.openFromComponent(NotificationBarComponent, {
        data: 'No content was added as it already exists in the content list',
      });
      return;
    }
    this.contentListStateService.changeContentList(true);
    this.viewContentListComponent.addToContentList(contents);
    this.notificationBar.openFromComponent(NotificationBarComponent, {
      data:
        contents.length === 1
          ? contents[0].title + ' was added to content list'
          : contents.length + ' contents were added to content list',
    });
  }

  triggerPinAndAddToContentList(contents: IContent[]): void {
    this.contentListStateService.changeContentList(true);
    this.viewContentListComponent.pinAndAddToContentList(contents);

    this.notificationBar.openFromComponent(NotificationBarComponent, {
      data:
        contents.length === 1
          ? contents[0].title + ' was pinned to content list'
          : contents.length + ' contents were pinned to content list',
    });
  }

  triggerTopicChangeToContentList(topics: ITopicBase[]): void {
    this.updateContentListRequest.topics = [];
    topics.forEach((x) => {
      this.updateContentListRequest.topics.push(x);
    });
  }

  triggerTagChangeToContentList(tags: ITag[]): void {
    this.updateContentListRequest.tags = [];
    tags.forEach((x) => {
      this.updateContentListRequest.tags.push(x);
    });
  }

  getUpdateContentListRequest(): IUpdateContentList {
    this.updateContentListRequest.contentPinned.push(
      ...this.viewContentListComponent.pinnedContent.map(
        (content, index) =>
          new ContentToPin(
            content.contentId,
            this.viewContentListComponent.pinnedContent.length - index
          )
      )
    );
    this.updateContentListRequest.contentRemoved.push(
      ...this.viewContentListComponent.contentRemoved.map(
        (content) =>
          new ContentToUpdate(content.contentId, content.contentVersionId)
      )
    );
    this.updateContentListRequest.contentAdded.push(
      ...this.viewContentListComponent.contentAdded.map(
        (content) =>
          new ContentToUpdate(content.contentId, content.contentVersionId)
      )
    );
    this.updateContentListRequest.removeAllPinnedContent =
      this.viewContentListComponent.removeAllPinnedContent &&
      !this.viewContentListComponent.pinnedContent.length;

    this.updateContentListRequest.name =
      this.contentListNamePrefix + ': ' + this._buildName();
    return this.updateContentListRequest;
  }

  updateContentListWithConcurrencyValidation(contentListId: string): void {
    this.isEditTitle = false;
    this.form.disable();
    this.contentListService
      .getById$(contentListId)
      .subscribe((contentListFromServer) => {
        if (this.contentList.cas === contentListFromServer.cas) {
          this.updateContentList(contentListId);
        } else {
          this.isContentListOutOfDate = true;
          this.dialogService.openConcurrencyClashDialog({
            message:
              'You are not working with the latest content list, please reload the page.',
          });
        }
      });
  }

  getLatestContentList(contentListId: string): void {
    this.contentListService
      .getById$(contentListId)
      .subscribe((contentListFromServer) => {
        this.contentList = contentListFromServer;
      });
  }

  openDeleteContentListDialog(contentListId: string): void {
    this.dialogService
      .openCustomComponentDialog(CmsConfirmationDialogComponent, {
        title: 'Confirm Deletion',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === null) {
          return;
        }

        if (result.toLowerCase() !== 'delete') {
          const prependText = result === '' ? 'No' : 'Incorrect';
          this.alertService.error(
            prependText +
              ' confirmation text entered, content list will not be deleted.'
          );
          return;
        }

        this.deleteContentList(contentListId);
      });
  }

  deleteContentList(contentListId: string): void {
    this.deleteSubmitted = true;
    this.contentListService.delete$(contentListId).subscribe({
      next: () => {
        this.deleteSubmitted = false;
        this.alertService.success('Content list has been deleted!');
        this.router.navigate(['./'], { relativeTo: this.route.parent });
      },
      error: () => {
        this.deleteSubmitted = false;
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst deleting the content list. Please contact IT',
        });
      },
    });
  }

  updateContentList(contentListId: string): void {
    this.submitted = true;
    const updateSubscription = this.contentListService
      .update$(this.getUpdateContentListRequest(), contentListId)
      .pipe(delay(this.updateTimerDuration))
      .subscribe(
        () => {
          this.contentListStateService.changeContentList(false);
          this.submitted = false;
          const viewContentPageSize =
            this.viewContentListComponent.pinnedContent.length +
            this.viewContentListComponent.unPinnedContent.length;
          this.viewContentListComponent.loadContent(viewContentPageSize);
          this.addContentListComponent.loadContent(
            this.addContentListComponent.query
          );
          this.updateContentListRequest = new UpdateContentList();
          this.alertService.success('Content list has been published');

          this.getLatestContentList(contentListId);
        },
        (error) => {
          this.submitted = false;
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst updating the content list. Please contact IT.',
          });
        }
      );
    this.pageSubscriptions.push(updateSubscription);
  }

  getName = () => {
    return this.contentListNameSuffix;
  };

  canDeactivate(): Observable<boolean> | boolean {
    if (this.contentListStateService.isContentListChanged()) {
      this.canDeactivateDialog.open(this.formChangeWarn, {
        disableClose: true,
        hasBackdrop: true,
        position: { top: 'calc(80px + 5%)' },
      });
      return this.changesDiscarded;
    }
    return true;
  }

  triggerUpdateAddContentListData(contents: IContent[]): void {
    contents.forEach((content) => {
      const row =
        this.addContentListComponent.contentDataSource.filteredData.find(
          (contentDataSourceRow) =>
            contentDataSourceRow.contentId === content.contentId
        );
      if (row) {
        row.isPinned = content.isPinned;
        row.isInContentList = content.isInContentList;
        row.isPendingPin = content.isPendingPin;
        row.pinPosition = content.pinPosition;
      }
    });
  }

  navigateAway(): void {
    this.changesDiscarded.next(true);
    this.contentListStateService.changeContentList(false);
    this.canDeactivateDialog.closeAll();
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.forEach((subscription) =>
      subscription.unsubscribe()
    );
    this.changesDiscarded.complete();
  }
}
